import React from 'react'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'
import BlockUi from 'react-block-ui'
import { lifecycle } from 'recompose'
import Row from '../../../bootstrap/component/Row'
import Column from '../../../bootstrap/component/Column'
import LabelInput from '../../../form/component/LabelInput'
import Input from '../../../form/component/Input'
import DatePicker from '../../../form/component/DatePicker'
import ContentBox from '../../../layout/component/ContentBox'
import Checkbox from '../../../form/component/Checkbox'
import TreatmentPreview from './TreatmentPreview'
import TextArea from '../../../form/component/TextArea'
import ValidationMessageList from '../../../validation/component/ValidationMessageList'
import { MAX_SCAN_FILE_SIZE } from '../../../customer/state/file-upload/const'
import {
    AVAILABLE_TREATMENT_PRICE_LIST,
    CURRENCY_CODE_LIST,
    CURRENCY_CODE_SYMBOL,
} from '../../state/const'
import Select from '../../../form/component/Select'
import {
    getQuotationCommentSelectValues,
    PACKAGESIZE_VALUES,
    QUOTATION_COMMENT_TEXT,
    PRETREATMENT_NEEDED_VALUES,
    ATTACHMENT_VALUES
} from '../../state/quotation/const'
import customerObjectType from '../../../../prop-types/customer-obj-type'
import treatmentQuotationObjectType from '../../../../prop-types/treatment-quotation-obj-type'
import currentUserHasAccess from '../../../../util/widget-helper'
import { TREATMENT_QUOTATION_WIDGET } from '../../../process/ui-definition/process-ui'
import store from '../../../store'
import { userHasAnyOfRoles } from '../../../security/state/auth/selector'
import {
    ROLE_EMPLOYEE_CUSTOMER_SERVICE, ROLE_QM_TREATMENT_PLANNER, ROLE_TREATMENT_PLANER, ROLE_TREATMENT_QUOTATION_ADMIN,
} from '../../../security/state/auth/const'
import { BACKEND_URL } from '../../../../util/api'

const moment = require('moment')

const getProlongValidDate = date =>
    moment(date)
        .add(23, 'hours')
        .add(59, 'minutes')
        .add(59, 'seconds')
        .format('YYYY-MM-DDTHH:mm:ss')

const style = {
    width: '100%',
    height: '200px',
    borderWidth: '2px',
    borderColor: 'rgb(102, 102, 102)',
    borderStyle: 'dashed',
    borderRadius: '5px',
    padding: '35px',
    textAlign: 'center',
    cursor: 'pointer',
    marginTop: '32px',
}
const buttonStyle = {
    marginTop: '24px',
    marginLeft: '12px',
}
const downloadPDFButtonStyle = {
    marginTop: '24px',
    marginBottom: '24px',
}
/**
 * this checks if the customer has an @sunshinesmile.de email. If so the purchased state can be reset manually
 * (To allow multiple treatment checkouts for testing)
 * @param customer
 */
const isSunshineSmileCustomer = customer => {
    if (!customer) {
        return false
    }
    if (!customer.email) {
        return false
    }
    return customer.email.indexOf('@sunshinesmile.de') !== -1
}

const getCommentText = (commentKey, countryCode = 'DE') => {
    const getLocalizedText = QUOTATION_COMMENT_TEXT[commentKey]
    if (getLocalizedText) {
        return getLocalizedText(countryCode)
    } else {
        return ''
    }
}
const EditAlignerButton = ({ setEditableForUser }) => (
    <button
        style={buttonStyle}
        className="btn btn-success btn-sm "
        onClick={setEditableForUser}
    >
        Edit quotation
    </button>
)
const UpdatePackageSizePriceButton = ({
    isUpdatePackagePriceEnabled,
    updatePackageAndPrice,
    treatmentQuotation,
    customer,
}) => (
    <button
        style={buttonStyle}
        className={[
            'btn btn-primary btn-sm',
            isUpdatePackagePriceEnabled ? '' : ' btn-secondary disabled',
        ].join(' ')}
        onClick={() => {
            if (isUpdatePackagePriceEnabled) {
                updatePackageAndPrice({
                    treatmentQuotation,
                    customer,
                })
            }
        }}
    >
        Update Package Size / BrutPrice
    </button>
)

const getComment = ({
    treatmentQuotation,
    changeTreatmentQuotation,
    saveTreatmentQuotation,
    customer,
}) => {
    const commentText = getCommentText(
        treatmentQuotation.commentKey,
        customer.countryCode,
    )
    changeTreatmentQuotation('comment', commentText)
    saveTreatmentQuotation()
}

const currentUserHasReadOnlyRole = () => {
    const canModifyTQRoles = [
        ROLE_EMPLOYEE_CUSTOMER_SERVICE,
        ROLE_TREATMENT_PLANER,
        ROLE_QM_TREATMENT_PLANNER,
        ROLE_TREATMENT_QUOTATION_ADMIN]
    const state = store.getState()
    return !userHasAnyOfRoles(state, canModifyTQRoles)
}

const TreatmentQuotation = ({
    treatmentQuotation,
    uploadingVideo,
    uploadVideoFile,
    isValid,
    changeTreatmentQuotation,
    saveTreatmentQuotation,
    customer,
    isPreviewModalOpen,
    openPreviewModal,
    closePreviewModal,
    validationMessageList,
    updatePackageAndPrice,
    isEditableForUser,
    hasUserRoleToEdit,
    setEditableForUser,
    treatmentPlan
}) => {
    if (!currentUserHasAccess(TREATMENT_QUOTATION_WIDGET)) {
        return null
    }

    if (!customer) {
        return null
    }

    if (customer.isImplantCustomer) {
        return null
    }

    if (!treatmentPlan) {
        const { activeTreatmentPlan, treatmentPlanList } = customer
        treatmentPlan = treatmentPlanList[activeTreatmentPlan]
    }

    const onChangeSelect = (name, value) => {
        changeTreatmentQuotation(name, value)
        saveTreatmentQuotation()
    }

    if (treatmentQuotation === null) {
        return <ContentBox title="Treatment Quotation"/>
    }

    const commentExists = treatmentQuotation && treatmentQuotation.commentKey && !treatmentQuotation.comment

    if (commentExists) {
        getComment({
            treatmentQuotation,
            changeTreatmentQuotation,
            saveTreatmentQuotation,
            customer,
        })
    }
    const currencyCodeSymbol = CURRENCY_CODE_SYMBOL[treatmentQuotation.currency]
    const isSunshineSmile = isSunshineSmileCustomer(customer)

    const hasPhysician = !!(
        customer.partnerPhysicianId || customer.treatingPhysicianId
    )
    const isPretreatmentNeeded = treatmentQuotation.pretreatmentNeeded
    const needsIPR = treatmentQuotation.iprNeeded
    const readonly = treatmentQuotation.purchased || currentUserHasReadOnlyRole()
    const isUpdatePackagePriceEnabled = !treatmentQuotation.purchased

    customer.treatmentPlanPDFList.sort((firstItem, secondItem) => {
        const firstItemCreationDate = firstItem.created
        const secondItemCreationDate = secondItem.created
        if (!firstItemCreationDate && !secondItemCreationDate) return 0
        if (!firstItemCreationDate && secondItemCreationDate) return -1
        if (firstItemCreationDate && !secondItemCreationDate) return 1
        return moment(secondItemCreationDate).unix() - moment(firstItemCreationDate).unix()
    })
    const isDownloadPDFButtonEnabled = (Object.keys(customer.treatmentPlanPDFList).length > 0)
    const pdfDownloadURL = isDownloadPDFButtonEnabled ? `${BACKEND_URL}/api/file-system/download-file?fileId=${customer.treatmentPlanPDFList[0].id}` : ''

    const title = 'Treatment Quotation  ' + treatmentQuotation.customerName
    return (
        <ContentBox title={title}>
            <TreatmentPreview
                treatmentQuotation={treatmentQuotation}
                isPreviewModalOpen={isPreviewModalOpen}
                closePreviewModal={closePreviewModal}
            />
            <Row>
                <Column size={12}>
                    <ValidationMessageList
                        validationMessageList={validationMessageList}
                    />
                </Column>
            </Row>

            {isSunshineSmile && (
                <Row>
                    <Column size={6}>
                        <Checkbox
                            object={treatmentQuotation}
                            name="purchased"
                            onChange={onChangeSelect}
                        >
                            Purchased
                        </Checkbox>
                    </Column>
                    <Column size={6}/>
                </Row>
            )}

            <Row>
                <Column size={6}>
                    <Checkbox
                        object={treatmentQuotation}
                        name="approved"
                        readOnly={!isValid || !hasPhysician || readonly}
                        onChange={onChangeSelect}
                    >
                        Allow customer to purchase
                    </Checkbox>
                    {!hasPhysician && (
                        <div className="form-error">
                            Please select a physician to activate this
                            quotation. (in the status area of the Sales Order)
                        </div>
                    )}
                </Column>
                <Column size={6}>
                    <Checkbox
                        object={treatmentQuotation}
                        name="ratePayAllowed"
                        readOnly={readonly}
                        onChange={onChangeSelect}
                    >
                        Rate pay allowed
                    </Checkbox>
                </Column>
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput label="Valid Until">
                        <DatePicker
                            object={treatmentQuotation}
                            name="validUntil"
                            readOnly={readonly}
                            onChange={(name, value) => {
                                changeTreatmentQuotation(
                                    name,
                                    getProlongValidDate(value),
                                )
                            }}
                            onBlur={saveTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
            </Row>

            <Row>
                <Column size={6}>
                    <LabelInput
                        label="Treatment duration (in weeks)"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Input
                            type="number"
                            object={treatmentQuotation}
                            readOnly={true}
                            name="treatmentDuration"
                            onChange={changeTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
                <Column size={6}>
                    <LabelInput
                        label="Number of aligner steps"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Input
                            type="number"
                            object={treatmentQuotation}
                            readOnly={true}
                            name="numberOfAligner"
                            onChange={changeTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
            </Row>

            <Row>
                <Column size={4}>
                    <LabelInput
                        label="Number of aligner lower jaw"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Input
                            type="number"
                            object={treatmentQuotation}
                            readOnly={readonly && !isEditableForUser}
                            name="numberOfAlignerLowerJaw"
                            onChange={changeTreatmentQuotation}
                            onBlur={saveTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
                <Column size={4}>
                    <LabelInput
                        label="Number of aligner upper jaw"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Input
                            type="number"
                            object={treatmentQuotation}
                            readOnly={readonly && !isEditableForUser}
                            name="numberOfAlignerUpperJaw"
                            onChange={changeTreatmentQuotation}
                            onBlur={saveTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
                <Column size={4}>
                    {hasUserRoleToEdit && treatmentQuotation.purchased && (
                        <EditAlignerButton
                            setEditableForUser={setEditableForUser}
                        />
                    )}

                    {isUpdatePackagePriceEnabled && (
                        <UpdatePackageSizePriceButton
                            {...{
                                updatePackageAndPrice,
                                isUpdatePackagePriceEnabled,
                                treatmentQuotation,
                                customer,
                            }}
                        />
                    )}
                </Column>
            </Row>

            <Row>
                <Column size={6}>
                    <LabelInput
                        label="Package Size"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Select
                            name="packageSize"
                            object={treatmentQuotation}
                            valueList={PACKAGESIZE_VALUES}
                            readOnly={readonly}
                            onChange={onChangeSelect}
                        />
                    </LabelInput>
                </Column>
                <Column size={6}>
                    <LabelInput label="IPR" groupAddOnIcon="fa fa-info">
                        <Input
                            object={treatmentQuotation}
                            name="iprNeeded"
                            readOnly={true}
                            placeholder={
                                needsIPR ? 'IPR required' : 'IPR Not required'
                            }
                        />
                    </LabelInput>
                </Column>
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput
                        label="Pretreatment"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Select
                            name="pretreatmentNeeded"
                            object={treatmentQuotation}
                            valueList={PRETREATMENT_NEEDED_VALUES}
                            readOnly={true}
                        />
                    </LabelInput>
                </Column>
                { treatmentPlan && <Column size={6}>
                    <LabelInput
                        label="Attachment"
                        groupAddOnIcon="fa fa-info"
                    >
                        <Select
                            name="attachmentNeeded"
                            object={treatmentPlan}
                            valueList={ATTACHMENT_VALUES}
                            readOnly={true}
                            addEmpty={true}
                        />
                    </LabelInput>
                </Column>}
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput
                        label="BrutPrice"
                        groupAddOnIcon={currencyCodeSymbol}
                    >
                        <Select
                            name="brutPrice"
                            object={treatmentQuotation}
                            valueList={AVAILABLE_TREATMENT_PRICE_LIST}
                            readOnly={readonly}
                            type="int"
                            onChange={onChangeSelect}
                        />
                    </LabelInput>
                </Column>
                <Column size={6}>
                    <LabelInput
                        label="Physician Cost"
                        groupAddOnIcon={currencyCodeSymbol}
                    >
                        <Input
                            object={treatmentQuotation}
                            name="physicianCost"
                            readOnly={true}
                            onChange={changeTreatmentQuotation}
                            onBlur={saveTreatmentQuotation}
                            isMoney={true}
                        />
                    </LabelInput>
                </Column>
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput label="Comment">
                        <Select
                            name="commentKey"
                            object={treatmentQuotation}
                            valueList={getQuotationCommentSelectValues(isPretreatmentNeeded)}
                            readOnly={readonly}
                            addEmpty={true}
                            onChange={(name, value) => {
                                changeTreatmentQuotation(name, value)
                                const commentText = getCommentText(
                                    value,
                                    customer.countryCode,
                                )
                                changeTreatmentQuotation('comment', commentText)
                                saveTreatmentQuotation()
                            }}
                        />
                    </LabelInput>
                </Column>
                <Column size={6}>
                    <LabelInput label="Currency">
                        <Select
                            name="currency"
                            object={treatmentQuotation}
                            valueList={CURRENCY_CODE_LIST}
                            readOnly={readonly}
                            addEmpty={false}
                            onChange={onChangeSelect}
                        />
                    </LabelInput>
                </Column>
            </Row>

            <Row>
                <Column size={9}>
                    <LabelInput
                        label="Treatment Link"
                        groupAddOnIcon="fa fa-info"
                        actionAddOnIcon="fa fa-search"
                        onActionAddOnClick={openPreviewModal}
                    >
                        <Input
                            object={treatmentQuotation}
                            name="treatmentLink"
                            readOnly={readonly && !isEditableForUser}
                            onChange={changeTreatmentQuotation}
                            onBlur={saveTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
                <Column size={3}>
                    <br/>
                    <a
                        href={treatmentQuotation.treatmentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Open in new Window
                    </a>
                </Column>
            </Row>
            {customer.countryCode === 'FR' && (
                <Row>
                    <Column>
                        <button
                            style={downloadPDFButtonStyle}
                            className={[
                                'btn btn-primary',
                                isDownloadPDFButtonEnabled ? '' : ' btn-secondary disabled',
                            ].join(' ')}
                            onClick={() => {
                                if (isDownloadPDFButtonEnabled) {
                                    window.open(pdfDownloadURL)
                                }
                            }}
                        >
                            Download Treatment Quotation PDF
                        </button>
                    </Column>
                </Row>
            )}
            <Row>
                <Column size={12}>
                    <LabelInput label="Comment">
                        <TextArea
                            name="comment"
                            readOnly={readonly && !isEditableForUser}
                            object={treatmentQuotation}
                            onChange={changeTreatmentQuotation}
                            onBlur={saveTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
            </Row>
            <Row>
                <Column size={12}>
                    {treatmentQuotation.id && !treatmentQuotation.purchased && (
                        <BlockUi tag="div" blocking={uploadingVideo}>
                            <Dropzone
                                multiple={false}
                                maxSize={MAX_SCAN_FILE_SIZE}
                                style={style}
                                disablePreview={true}
                                onDrop={fileList => {
                                    uploadVideoFile(
                                        fileList[0],
                                        treatmentQuotation.id,
                                    )
                                }}
                            >
                                <p>Click to upload a video</p>
                            </Dropzone>
                        </BlockUi>
                    )}
                </Column>
            </Row>
        </ContentBox>
    )
}

TreatmentQuotation.propTypes = {
    updatePackageAndPrice: PropTypes.func.isRequired,
    treatmentQuotation: treatmentQuotationObjectType,
    isPreviewModalOpen: PropTypes.bool.isRequired,
    isValid: PropTypes.bool,
    findOrCreateTreatmentQuotation: PropTypes.func.isRequired,
    changeTreatmentQuotation: PropTypes.func.isRequired,
    saveTreatmentQuotation: PropTypes.func.isRequired,
    openPreviewModal: PropTypes.func.isRequired,
    closePreviewModal: PropTypes.func.isRequired,
    validationMessageList: PropTypes.array.isRequired,
    uploadVideoFile: PropTypes.func.isRequired,
    uploadingVideo: PropTypes.bool.isRequired,
    customer: customerObjectType,
    isEditableForUser: PropTypes.bool.isRequired,
    hasUserRoleToEdit: PropTypes.bool.isRequired,
    setEditableForUser: PropTypes.func.isRequired,
}

export default lifecycle({
    componentDidMount () {
        if (this.props.onlyDisplay) {
            return
        }
        if (!this.props.customer.isImplantCustomer) {
            this.props.findOrCreateTreatmentQuotation(this.props.productType)
        }
    },
})(TreatmentQuotation)





// WEBPACK FOOTER //
// ./src/modules/treatment/component/quotation/TreatmentQuotation.js